import { LoanType } from "@/modules/loan/common/enum";
import type { ILoan } from "@/modules/loan/common/types";
import { Amount } from "@/shared/components";
import { CollapseItem } from "@ab.uitools/ui-library/components/Collapse";
import { Copy } from "@ab.uitools/ui-library/components/Copy";
import { Divider } from "@ab.uitools/ui-library/components/Divider";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { type RefObject, useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  loan?: ILoan;
  scrollbarContainerRef?: RefObject<HTMLDivElement | null>;
}

export const LoanDetailRows = ({ loan, scrollbarContainerRef }: Props) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const toggleCollapse = () => setIsOpen(!isOpen);
  const isFactoring = loan?.loanType === LoanType.Factoring;

  return (
    <div>
      <div>
        <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
          <Text type="secondary" size="medium" weight="semibold">
            {t("loans.loanCode")}
          </Text>
          <div className="flexbox align-items--center">
            <Copy
              textAfterCopy={t("general.copied")}
              size="medium"
              parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
              text={loan?.code as string}
            />
            <Text size="medium" weight="bold">{loan?.code}</Text>
          </div>
        </div>
        <Divider
          color="light"
          type="primary"
          isHorizontal
        />
      </div>
      <div>
        <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
          <Text type="secondary" size="medium" weight="semibold">
            {t("general.contractNumber")}
          </Text>
          <div className="flexbox align-items--center">
            <Copy
              textAfterCopy={t("general.copied")}
              size="medium"
              parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
              text={loan?.contractNumber as string}
            />
            <Text size="medium" weight="bold">{loan?.contractNumber}</Text>
          </div>
        </div>
        <Divider
          color="light"
          type="primary"
          isHorizontal
        />
      </div>
      <div>
        <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
          <Text type="secondary" size="medium" weight="semibold">
            {t("loans.principalAmount")}
          </Text>
          <Amount
            currency={loan?.currency}
            amount={loan?.principalAmount as number}
          />
        </div>
        <Divider
          color="light"
          type="primary"
          isHorizontal
        />
      </div>
      <div>
        <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
          <Text type="secondary" size="medium" weight="semibold">
            {t("loans.loanBalance")}
          </Text>
          <Amount
            currency={loan?.currency}
            amount={loan?.remainingPrincipalAmount as number}
          />
        </div>
        <Divider
          color="light"
          type="primary"
          isHorizontal
        />
      </div>
      <div>
        <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
          <Text type="secondary" size="medium" weight="semibold">
            {t(isFactoring ? "loans.factoringEndDate" : "loans.nextPayment")}
          </Text>
          <Text size="medium" weight="bold">
            {isFactoring ? loan?.endDate : loan?.upcomingPaymentDate}
          </Text>
        </div>
        <Divider
          color="light"
          type="primary"
          isHorizontal
        />
      </div>
      {isFactoring && (
        <div>
          <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
            <Text type="secondary" size="medium" weight="semibold">
              {t("loans.regularRepayment")}
            </Text>
            <Amount
              currency={loan?.currency}
              amount={loan?.upcomingPaymentAmount as number}
            />
          </div>
          <Divider
            color="light"
            type="primary"
            isHorizontal
          />
        </div>
      )}
      <div>
        <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
          <Text type="secondary" size="medium" weight="semibold">
            {t("loans.accumulatedInterestAmount")}
          </Text>
          <Amount
            currency={loan?.currency}
            amount={loan?.accumulatedInterestAmount as number}
          />
        </div>
        <Divider
          color="light"
          type="primary"
          isHorizontal
        />
      </div>
      <CollapseItem
        className="detail-collapse"
        isOpen={isOpen}
        toggle={toggleCollapse}
        title={t("general.additionalDetails")}
      >
        <div>
          <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
            <Text type="secondary" size="medium" weight="semibold">
              {t("loans.interestRate")}
            </Text>
            <Text size="medium" weight="bold">{loan?.interestRate}%</Text>
          </div>
          <Divider
            color="light"
            type="primary"
            isHorizontal
          />
        </div>
        <div>
          <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
            <Text type="secondary" size="medium" weight="semibold">
              {t("loans.creationDate")}
            </Text>
            <Text size="medium" weight="bold">
              {loan?.creationDate}
            </Text>
          </div>
          <Divider
            color="light"
            type="primary"
            isHorizontal
          />
        </div>
        <div>
          <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
            <Text type="secondary" size="medium" weight="semibold">
              {t("loans.endDate")}
            </Text>
            <Text size="medium" weight="bold">
              {loan?.endDate}
            </Text>
          </div>
        </div>
      </CollapseItem>
      <Divider
        color="light"
        type="primary"
        isHorizontal
      />
    </div>
  );
};
